import {
    Code2,
    Columns2,
    Eye,
    Moon,
    PanelLeft,
    PanelLeftClose,
    PanelRight,
    PanelRightClose,
    Sun,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import { memo, useEffect, useMemo, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import { cn } from "../lib/utils";
import { useEditorStore, type ViewMode } from "../store/editor-store";
import { useThemeStore } from "../theme/runtime/theme-store";

const viewModes: { mode: ViewMode; icon: React.ReactNode; labelKey: string }[] = [
    { mode: "editor", icon: <Code2 size={15} />, labelKey: "toolbar.view.editor" },
    { mode: "split", icon: <Columns2 size={15} />, labelKey: "toolbar.view.split" },
    { mode: "preview", icon: <Eye size={15} />, labelKey: "toolbar.view.preview" },
];

/* Whether we're running inside Tauri (desktop) */
function isTauri(): boolean {
    return "__TAURI_INTERNALS__" in window;
}

const ViewModeControls = memo(function ViewModeControls() {
    const t = useT("editor");
    const {
        focusedPaneId,
        focusedPaneViewMode,
        hasFocusedPane,
        globalViewMode,
    } = useEditorStore(
        useShallow((s) => {
            const focusedPane = s.panes?.find((p) => p.paneId === s.focusedPaneId);
            return {
                focusedPaneId: s.focusedPaneId,
                focusedPaneViewMode: focusedPane?.viewMode,
                hasFocusedPane: Boolean(focusedPane),
                globalViewMode: s.viewMode,
            };
        }),
    );
    const setPaneViewMode = useEditorStore((s) => s.setPaneViewMode);
    const setGlobalViewMode = useEditorStore((s) => s.setViewMode);
    const viewMode = focusedPaneViewMode ?? globalViewMode;
    const setViewMode = useMemo(() => {
        return (mode: ViewMode) => {
            if (hasFocusedPane) {
                setPaneViewMode(focusedPaneId, mode);
            } else {
                setGlobalViewMode(mode);
            }
        };
    }, [hasFocusedPane, focusedPaneId, setPaneViewMode, setGlobalViewMode]);

    return (
        <div className="flex items-center bg-muted rounded-lg p-0.5 gap-0.5">
            {viewModes.map(({ mode, icon, labelKey }) => {
                const label = t(labelKey);
                return (
                <button
                    type="button"
                    key={mode}
                    data-testid={`view-mode-${mode}`}
                    onClick={() => setViewMode(mode)}
                    className={cn(
                        "flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors",
                        viewMode === mode
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                    )}
                    title={label}
                >
                    {icon}
                    <span className="hidden sm:inline">{label}</span>
                </button>
                );
            })}
        </div>
    );
});

const SidebarToggleButtons = memo(function SidebarToggleButtons() {
    const t = useT("editor");
    const toggleSidebar = useEditorStore((s) => s.toggleSidebar);
    const toggleRightSidebar = useEditorStore((s) => s.toggleRightSidebar);
    const rightSidebarOpen = useEditorStore((s) => s.rightSidebarOpen);
    const sidebarOpen = useEditorStore((s) => s.sidebarOpen);

    return (
        <>
            <button
                type="button"
                onClick={toggleSidebar}
                className={cn(
                    "p-1.5 rounded-md hover:bg-muted transition-colors",
                    sidebarOpen
                        ? "text-primary hover:text-primary"
                        : "text-muted-foreground hover:text-foreground",
                )}
                title={t("toolbar.toggleSidebar")}
            >
                {sidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
            </button>
            <button
                type="button"
                onClick={toggleRightSidebar}
                className={cn(
                    "p-1.5 rounded-md hover:bg-muted transition-colors",
                    rightSidebarOpen
                        ? "text-primary hover:text-primary"
                        : "text-muted-foreground hover:text-foreground",
                )}
                title={t("toolbar.toggleConfigSidebar")}
            >
                {rightSidebarOpen ? (
                    <PanelRightClose size={16} />
                ) : (
                    <PanelRight size={16} />
                )}
            </button>
        </>
    );
});

const ThemeToggleButton = memo(function ThemeToggleButton() {
    const t = useT("editor");
    const resolvedColorScheme = useThemeStore((s) => s.resolvedColorScheme);
    const toggleColorTheme = useThemeStore((s) => s.toggleColorTheme);

    return (
        <button
            type="button"
            onClick={() => void toggleColorTheme()}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={
                resolvedColorScheme === "dark"
                    ? t("toolbar.theme.light")
                    : t("toolbar.theme.dark")
            }
        >
            {resolvedColorScheme === "dark" ? (
                <Sun size={16} />
            ) : (
                <Moon size={16} />
            )}
        </button>
    );
});

export interface ToolbarProps {
    /** Extra content rendered before the view-mode buttons (e.g. PushButton in Tauri) */
    toolbarSlot?: React.ReactNode;
    /** Start dragging the window (provided by Tauri host) */
    onStartDragging?: () => void;
    /** Toggle window maximize/restore (provided by Tauri host) */
    onToggleMaximize?: () => void;
    /** A title displayed in the toolbar (e.g. filename for detached windows) */
    titleText?: string;
    /** Rich title slot — replaces titleText when provided (e.g. icon + name + action) */
    titleSlot?: React.ReactNode;
}

export const Toolbar = memo(function Toolbar({
    toolbarSlot,
    onStartDragging,
    onToggleMaximize,
    titleText,
    titleSlot,
}: ToolbarProps) {
    const inTauri = isTauri();
    const headerRef = useRef<HTMLElement>(null);

    /* ── Window dragging & double-click-to-zoom for custom title bar ── */
    // Use a native DOM listener (not React synthetic event) for reliable
    // interaction with Tauri's startDragging API.
    // The host app passes onStartDragging / onToggleMaximize callbacks.
    useEffect(() => {
        if (!onStartDragging && !onToggleMaximize) return;
        const header = headerRef.current;
        if (!header) return;

        const onMouseDown = (e: MouseEvent) => {
            // Don't drag when clicking interactive elements
            if (
                (e.target as HTMLElement).closest("button, input, select, textarea, a")
            )
                return;
            if (e.buttons !== 1) return; // Only primary (left) button
            if (e.detail === 2) {
                // Double-click → toggle maximize (native macOS zoom behavior)
                onToggleMaximize?.();
            } else {
                onStartDragging?.();
            }
        };

        header.addEventListener("mousedown", onMouseDown);
        return () => header.removeEventListener("mousedown", onMouseDown);
    }, [onStartDragging, onToggleMaximize]);

    return (
        <header
            ref={headerRef}
            data-testid="toolbar"
            className="flex items-center h-11 px-2 border-b border-border bg-background shrink-0 select-none gap-1"
        >
            {/* macOS: leave space for native traffic lights (overlay mode) */}
            {inTauri && <div className="w-[70px] shrink-0" />}

            <img
                src="/app-icon.png"
                alt="Magic MD"
                title="Magic MD"
                className="h-5 w-5 shrink-0 rounded-[5px]"
                draggable={false}
            />

            {/* Document title (detached windows) */}
            {titleSlot ??
                (titleText && (
                    <span
                        className="text-xs font-medium text-foreground/80 truncate max-w-[260px]"
                        title={titleText}
                    >
                        {titleText}
                    </span>
                ))}

            {/* Center spacer */}
            <div className="flex-1" />

            {/* Host-provided toolbar slot (e.g. PushButton) */}
            {toolbarSlot}

            <div className="w-px h-5 bg-border mx-1" />

            {/* Right: view mode + dark mode */}
            <ViewModeControls />

            {/* Sidebar toggles (left + right) */}
            <div className="w-px h-5 bg-border mx-1" />
            <SidebarToggleButtons />

            <div className="w-px h-5 bg-border mx-1" />

            <ThemeToggleButton />
        </header>
    );
});
